import React, { useState, useEffect } from 'react';
import { useApi, apiClient } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { 
  Users, Plus, TrendingUp, TrendingDown, Building2, ChevronDown, ChevronRight,
  ChevronUp, ChevronsUpDown
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#0066CC', '#00AA66', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// Legacy static config for fallback
const FAIXAS_ETARIAS_LEGACY = [
  { key: 'bebes_0_12m', label: 'Bebés (0-12m)' },
  { key: 'bebes_13_24m', label: 'Bebés (13-24m)' },
  { key: 'criancas_3_5a', label: 'Crianças (3-5a)' },
  { key: 'criancas_6_10a', label: 'Crianças (6-10a)' },
  { key: 'jovens_11_14a', label: 'Jovens (11-14a)' },
  { key: 'jovens_15_18a', label: 'Jovens (15-18a)' },
  { key: 'adultos_18plus', label: 'Adultos (18+)' },
  { key: 'idosos_65plus', label: 'Idosos (65+)' },
];

export default function UtentesPage() {
  const api = useApi();
  const [utentes, setUtentes] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  const [valencias, setValencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  
  // Dynamic age groups from API
  const [faixasEtarias, setFaixasEtarias] = useState([]);
  const [faixasEtariasValencia, setFaixasEtariasValencia] = useState([]);
  const [loadingFaixas, setLoadingFaixas] = useState(false);
  
  // Form state
  const [selectedValencia, setSelectedValencia] = useState('');
  
  const [formData, setFormData] = useState({
    instituicao_id: '',
    valencia_id: '',
    periodo_referencia: new Date().toISOString().slice(0, 7),
    fonte_dados: 'Manual'
  });
  
  // Dynamic form fields based on faixas etárias
  const [formDemografia, setFormDemografia] = useState({});
  
  // Collapsible state for institutions
  const [expandedInstitutions, setExpandedInstitutions] = useState({});
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'instituicao_nome', direction: 'asc' });

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon for column
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronsUpDown className="w-4 h-4 text-slate-400 ml-1" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-[#0066CC] ml-1" />
      : <ChevronDown className="w-4 h-4 text-[#0066CC] ml-1" />;
  };
  
  const toggleInstitution = (id) => {
    setExpandedInstitutions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  const expandAll = () => {
    const allExpanded = {};
    utentes.forEach(u => { allExpanded[u.id] = true; });
    setExpandedInstitutions(allExpanded);
  };
  
  const collapseAll = () => {
    setExpandedInstitutions({});
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [utentesData, instData, valenciasData, faixasRes] = await Promise.all([
        api.getUtentesAgregados(),
        api.getInstituicoes(),
        api.getValencias(),
        apiClient.get('/demografia/faixas-etarias')
      ]);
      setUtentes(utentesData || []);
      
      // Handle instituicoes - could be array directly or wrapped in object
      const insts = Array.isArray(instData) ? instData : (instData?.instituicoes || []);
      setInstituicoes(insts);
      
      setValencias(valenciasData || []);
      
      // Process faixas etárias
      const faixas = faixasRes?.data || [];
      setFaixasEtarias(faixas);
      
      // Initialize form demografia fields
      const demografiaInit = {};
      faixas.forEach(f => {
        demografiaInit[`${f.codigo}_masculino`] = 0;
        demografiaInit[`${f.codigo}_feminino`] = 0;
      });
      setFormDemografia(demografiaInit);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };
  
  // Load valencia-specific faixas when valencia changes
  const loadFaixasForValencia = async (valenciaId) => {
    if (!valenciaId) {
      setFaixasEtariasValencia([]);
      return;
    }
    
    setLoadingFaixas(true);
    try {
      const response = await apiClient.get(`/demografia/valencias/${valenciaId}/faixas-etarias`);
      const data = response?.data || {};
      setFaixasEtariasValencia(data?.faixas_etarias || []);
    } catch (error) {
      console.error('Error loading valencia faixas:', error);
      // Fallback to all faixas
      setFaixasEtariasValencia([]);
    } finally {
      setLoadingFaixas(false);
    }
  };
  
  const handleValenciaChange = (valenciaId) => {
    // Handle special value for "all"
    const actualId = valenciaId === '__all__' ? '' : valenciaId;
    setSelectedValencia(actualId);
    setFormData(prev => ({ ...prev, valencia_id: actualId }));
    loadFaixasForValencia(actualId);
  };
  
  // Get current faixas to display (valencia-specific or all)
  const getCurrentFaixas = () => {
    if (selectedValencia && faixasEtariasValencia.length > 0) {
      return faixasEtariasValencia;
    }
    return faixasEtarias;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.instituicao_id) {
      toast.error('Selecione uma instituição');
      return;
    }
    
    setFormLoading(true);
    try {
      // Combine form data with demographic data
      const submitData = {
        ...formData,
        ...formDemografia
      };
      await api.createUtentesAgregados(submitData);
      toast.success('Dados guardados com sucesso!');
      setModalOpen(false);
      loadData();
      resetForm();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erro ao guardar dados');
    } finally {
      setFormLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      instituicao_id: '',
      valencia_id: '',
      periodo_referencia: new Date().toISOString().slice(0, 7),
      fonte_dados: 'Manual'
    });
    setSelectedValencia('');
    setFaixasEtariasValencia([]);
    // Reset demographic fields
    const demografiaInit = {};
    faixasEtarias.forEach(f => {
      demografiaInit[`${f.codigo}_masculino`] = 0;
      demografiaInit[`${f.codigo}_feminino`] = 0;
    });
    setFormDemografia(demografiaInit);
  };

  // Calculate totals for charts - use dynamic faixas or fallback to legacy
  const faixasParaCalculo = faixasEtarias.length > 0 
    ? faixasEtarias.map(f => ({ key: f.codigo, label: f.nome }))
    : FAIXAS_ETARIAS_LEGACY;
    
  const totaisPorFaixa = faixasParaCalculo.map(faixa => {
    const totalM = utentes.reduce((sum, u) => sum + (u[`${faixa.key}_masculino`] || 0), 0);
    const totalF = utentes.reduce((sum, u) => sum + (u[`${faixa.key}_feminino`] || 0), 0);
    return {
      name: faixa.label,
      masculino: totalM,
      feminino: totalF,
      total: totalM + totalF
    };
  });

  const totalGeral = utentes.reduce((sum, u) => sum + (u.total_utentes || 0), 0);
  const totalMasculino = utentes.reduce((sum, u) => sum + (u.total_masculino || 0), 0);
  const totalFeminino = utentes.reduce((sum, u) => sum + (u.total_feminino || 0), 0);

  const generoData = [
    { name: 'Masculino', value: totalMasculino },
    { name: 'Feminino', value: totalFeminino }
  ];

  // Pyramid data (reversed for visualization)
  const pyramidData = totaisPorFaixa.map(faixa => ({
    name: faixa.name,
    masculino: -faixa.masculino, // Negative for left side
    feminino: faixa.feminino
  })).reverse();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="utentes-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="utentes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Utentes Agregados</h1>
          <p className="text-slate-500 mt-1">Dados agregados de pessoas apoiadas (RGPD compliant)</p>
        </div>
        <Button 
          onClick={() => setModalOpen(true)}
          className="bg-[#0066CC] hover:bg-[#0052a3]"
          data-testid="add-utentes-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Registar Dados
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-metric" data-testid="total-utentes-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Pessoas</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">
                  {totalGeral.toLocaleString('pt-PT')}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#0066CC]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#0066CC]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Masculino</p>
                <p className="text-3xl font-bold text-[#0066CC] mt-2">
                  {totalMasculino.toLocaleString('pt-PT')}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {totalGeral > 0 ? ((totalMasculino / totalGeral) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#0066CC]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Feminino</p>
                <p className="text-3xl font-bold text-[#00AA66] mt-2">
                  {totalFeminino.toLocaleString('pt-PT')}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {totalGeral > 0 ? ((totalFeminino / totalGeral) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-[#00AA66]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Registos</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">
                  {utentes.length}
                </p>
                <p className="text-xs text-slate-400 mt-1">por período</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Badge className="bg-purple-500">{utentes.length}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Population Pyramid */}
        <Card data-testid="pyramid-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Pirâmide Etária</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={pyramidData}
                  margin={{ top: 20, right: 30, left: 80, bottom: 5 }}
                  stackOffset="sign"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    type="number" 
                    tickFormatter={(value) => Math.abs(value).toString()}
                    tick={{ fill: '#64748B', fontSize: 12 }}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fill: '#64748B', fontSize: 11 }}
                    width={75}
                  />
                  <Tooltip 
                    formatter={(value) => Math.abs(value).toLocaleString('pt-PT')}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="masculino" name="Masculino" fill="#0066CC" stackId="stack" />
                  <Bar dataKey="feminino" name="Feminino" fill="#00AA66" stackId="stack" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Gender Pie Chart */}
        <Card data-testid="gender-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Distribuição por Género</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={generoData}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={140}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#0066CC" />
                    <Cell fill="#00AA66" />
                  </Pie>
                  <Tooltip 
                    formatter={(value) => value.toLocaleString('pt-PT')}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Age Distribution Bar Chart */}
      <Card data-testid="age-distribution-chart">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Distribuição por Faixa Etária</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={totaisPorFaixa} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fill: '#64748B', fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
                <Tooltip 
                  formatter={(value) => value.toLocaleString('pt-PT')}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="masculino" name="Masculino" fill="#0066CC" radius={[4, 4, 0, 0]} />
                <Bar dataKey="feminino" name="Feminino" fill="#00AA66" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card data-testid="utentes-table-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Registos por Instituição</CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={expandAll}
              className="text-xs"
            >
              <ChevronDown className="w-3 h-3 mr-1" />
              Expandir Todos
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={collapseAll}
              className="text-xs"
            >
              <ChevronRight className="w-3 h-3 mr-1" />
              Colapsar Todos
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('instituicao_nome')}
                >
                  <div className="flex items-center">
                    Instituição / Resposta Social {getSortIcon('instituicao_nome')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('periodo_referencia')}
                >
                  <div className="flex items-center">
                    Período {getSortIcon('periodo_referencia')}
                  </div>
                </TableHead>
                <TableHead 
                  className="text-center cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('total_utentes')}
                >
                  <div className="flex items-center justify-center">
                    Total {getSortIcon('total_utentes')}
                  </div>
                </TableHead>
                <TableHead 
                  className="text-center cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('total_masculino')}
                >
                  <div className="flex items-center justify-center">
                    Masculino {getSortIcon('total_masculino')}
                  </div>
                </TableHead>
                <TableHead 
                  className="text-center cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('total_feminino')}
                >
                  <div className="flex items-center justify-center">
                    Feminino {getSortIcon('total_feminino')}
                  </div>
                </TableHead>
                <TableHead>Fonte</TableHead>
                <TableHead>Validado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {utentes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    Nenhum registo encontrado
                  </TableCell>
                </TableRow>
              ) : (
                [...utentes].sort((a, b) => {
                  if (!sortConfig.key) return 0;
                  
                  let aVal = a[sortConfig.key];
                  let bVal = b[sortConfig.key];
                  
                  if (aVal == null) aVal = '';
                  if (bVal == null) bVal = '';
                  
                  if (typeof aVal === 'string' && typeof bVal === 'string') {
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                  }
                  
                  if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                  if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                  return 0;
                }).map((u) => {
                  const inst = instituicoes.find(i => i.id === u.instituicao_id);
                  const hasValencias = u.valencias && u.valencias.length > 0;
                  const isExpanded = expandedInstitutions[u.id];
                  
                  return (
                    <React.Fragment key={u.id}>
                      {/* Institution Row (Parent) */}
                      <TableRow 
                        className={`bg-slate-100 hover:bg-slate-200 font-semibold ${hasValencias ? 'cursor-pointer' : ''}`}
                        onClick={() => hasValencias && toggleInstitution(u.id)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {hasValencias && (
                              <span className="text-slate-500">
                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </span>
                            )}
                            <Building2 className="w-4 h-4 text-[#0066CC]" />
                            <span>{u.instituicao_nome || inst?.nome || 'N/A'}</span>
                            {u.instituicao_codigo && (
                              <Badge variant="outline" className="text-xs">{u.instituicao_codigo}</Badge>
                            )}
                            {hasValencias && (
                              <Badge className="bg-slate-200 text-slate-600 text-xs ml-2">
                                {u.valencias.length} resposta{u.valencias.length !== 1 ? 's sociais' : ' social'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{u.periodo_referencia}</TableCell>
                        <TableCell className="text-center font-bold text-slate-900">
                          {u.total_utentes?.toLocaleString('pt-PT')}
                        </TableCell>
                        <TableCell className="text-center text-[#0066CC] font-bold">
                          {u.total_masculino?.toLocaleString('pt-PT')}
                        </TableCell>
                        <TableCell className="text-center text-[#00AA66] font-bold">
                          {u.total_feminino?.toLocaleString('pt-PT')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{u.fonte_dados}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={u.validado ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {u.validado ? 'Sim' : 'Pendente'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                      
                      {/* Valencia Rows (Children) - Only show if expanded */}
                      {hasValencias && isExpanded && u.valencias.map((v, idx) => (
                        <TableRow key={`${u.id}-val-${idx}`} className="hover:bg-slate-50 text-sm animate-fade-in">
                          <TableCell className="pl-12">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-300">└</span>
                              <Users className="w-3 h-3 text-slate-400" />
                              <span className="text-slate-700">{v.valencia_nome}</span>
                              <Badge variant="outline" className="text-xs font-normal">{v.valencia_codigo}</Badge>
                              {v.categoria && (
                                <span className="text-xs text-slate-400">({v.categoria})</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-500">-</TableCell>
                          <TableCell className="text-center text-slate-700">
                            {v.numero_utentes?.toLocaleString('pt-PT')}
                          </TableCell>
                          <TableCell className="text-center text-[#0066CC]/70">
                            {v.utentes_masculino?.toLocaleString('pt-PT')}
                          </TableCell>
                          <TableCell className="text-center text-[#00AA66]/70">
                            {v.utentes_feminino?.toLocaleString('pt-PT')}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-slate-400">
                              {v.capacidade_maxima ? `Cap: ${v.capacidade_maxima}` : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {v.ativa !== false ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">Ativa</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700 text-xs">Inativa</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Data Modal */}
      <Dialog open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="utentes-modal">
          <DialogHeader>
            <DialogTitle>Registar Dados de Utentes</DialogTitle>
            <DialogDescription>
              Introduza os dados agregados por faixa etária e género (RGPD compliant)
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Instituição *</Label>
                <Select 
                  value={formData.instituicao_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, instituicao_id: v }))}
                >
                  <SelectTrigger data-testid="select-instituicao">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {instituicoes.map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Período de Referência *</Label>
                <Input
                  type="month"
                  value={formData.periodo_referencia}
                  onChange={(e) => setFormData(prev => ({ ...prev, periodo_referencia: e.target.value }))}
                  data-testid="input-periodo"
                />
              </div>
            </div>
            
            {/* Valencia selector - filters available faixas etárias */}
            <div className="border rounded-lg p-4 bg-slate-50">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-5 h-5 text-slate-600" />
                <Label className="font-semibold text-slate-900">Resposta Social (Opcional)</Label>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Selecione uma resposta social para filtrar as faixas etárias relevantes. Se não selecionar, todas as faixas serão mostradas.
              </p>
              <Select 
                value={selectedValencia} 
                onValueChange={handleValenciaChange}
              >
                <SelectTrigger data-testid="select-valencia">
                  <SelectValue placeholder="Todas as faixas etárias..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas as faixas etárias</SelectItem>
                  {valencias.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.nome} {v.categoria && <span className="text-slate-400">({v.categoria})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {loadingFaixas && (
                <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                  <div className="w-4 h-4 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
                  A carregar faixas etárias...
                </div>
              )}
            </div>

            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-4 text-slate-900">
                Dados por Faixa Etária e Género
                {selectedValencia && faixasEtariasValencia.length > 0 && (
                  <Badge variant="outline" className="ml-2 font-normal">
                    {faixasEtariasValencia.length} faixas para esta resposta social
                  </Badge>
                )}
              </h4>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm font-medium text-slate-500 border-b pb-2">
                  <div>Faixa Etária</div>
                  <div className="text-center text-[#0066CC]">Masculino</div>
                  <div className="text-center text-[#00AA66]">Feminino</div>
                </div>
                {getCurrentFaixas().map(faixa => (
                  <div key={faixa.id || faixa.codigo} className="grid grid-cols-3 gap-4 items-center">
                    <div className="text-sm font-medium">{faixa.nome}</div>
                    <Input
                      type="number"
                      min="0"
                      value={formDemografia[`${faixa.codigo}_masculino`] || 0}
                      onChange={(e) => setFormDemografia(prev => ({ 
                        ...prev, 
                        [`${faixa.codigo}_masculino`]: parseInt(e.target.value) || 0 
                      }))}
                      className="text-center"
                      data-testid={`input-${faixa.codigo}-m`}
                    />
                    <Input
                      type="number"
                      min="0"
                      value={formDemografia[`${faixa.codigo}_feminino`] || 0}
                      onChange={(e) => setFormDemografia(prev => ({ 
                        ...prev, 
                        [`${faixa.codigo}_feminino`]: parseInt(e.target.value) || 0 
                      }))}
                      className="text-center"
                      data-testid={`input-${faixa.codigo}-f`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Fonte dos Dados</Label>
              <Select 
                value={formData.fonte_dados} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, fonte_dados: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Manual">Entrada Manual</SelectItem>
                  <SelectItem value="Importação">Importação Excel</SelectItem>
                  <SelectItem value="API">Integração API</SelectItem>
                  <SelectItem value="Campanha">Campanha Atualização</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-[#0066CC] hover:bg-[#0052a3]"
                disabled={formLoading}
                data-testid="submit-utentes-btn"
              >
                {formLoading ? 'A guardar...' : 'Guardar Dados'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
